import React, { useState, useRef, useEffect } from 'react';
import { PlayArrow, Pause } from '@mui/icons-material';
import { Box, IconButton, Slider, Stack, Typography } from '@mui/material';

const AudioExtractPlayer = ({ audioUrl, start, end }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  const startTime = start;
  const endTime = end;
  const extractDuration = endTime - startTime;

  useEffect(() => {
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const handleMetadataLoaded = () => null;

    const handleTimeUpdate = () => {
      const relativeTime = audio.currentTime - startTime;
      setCurrentTime(relativeTime >= 0 ? relativeTime : 0);

      if (audio.currentTime >= endTime) {
        audio.pause();
        audio.currentTime = startTime;
        setIsPlaying(false);
        setCurrentTime(0);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', handleMetadataLoaded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.pause();
      audio.src = '';
      audio.removeEventListener('loadedmetadata', handleMetadataLoaded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl, startTime, endTime]);

  const togglePlay = () => {
    if (!audioRef.current) {
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      if (currentTime >= extractDuration) {
        audioRef.current.currentTime = startTime;
        setCurrentTime(0);
      } else if (audioRef.current.currentTime < startTime ||
        audioRef.current.currentTime > endTime) {
        audioRef.current.currentTime = startTime;
      }
      audioRef.current.play();
    }

    setIsPlaying(!isPlaying);
  };

  const handleSliderChange = (_, newValue) => {
    if (!audioRef.current) {
      return;
    }

    const newTime = startTime + newValue;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newValue);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton
          onClick={togglePlay}
          size="small"
          color="primary"
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>

        <Slider
          size="small"
          value={currentTime}
          max={extractDuration}
          onChange={handleSliderChange}
          aria-label="Progression audio"
          sx={{ mx: 1 }}
        />

        <Typography variant="caption" color="text.secondary">
          {formatTime(currentTime)}/{formatTime(extractDuration)}
        </Typography>
      </Stack>
    </Box>
  );
};

export default AudioExtractPlayer;
